import { useEffect, useMemo, useCallback, useRef, useState, memo } from 'react';
import {
  Badge,
  Tabs,
  Tab,
  Stack,
  Box,
  Divider,
  Button,
  Paper,
  Fab,
} from '@mui/material';
import { useLoaderData, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import Header from '../components/restaurant/Header';
import { MenuItem } from '../components/restaurant/Menu';
import OrderDialog from '../components/restaurant/Order';
import TipDialog from '../components/restaurant/Tip';
import PaymentDialog from '../components/restaurant/Payment';
import StatusDialog from '../components/restaurant/Status';
import { loadCompanyById, loadCompanyByName } from '../actions/restaurant/company';
import { decodeBase64UUID } from '../security/encdec64';
import { Footer } from '../components/restaurant/Footer';
import { useDialog } from '../hooks/useDialog';
import { Alerts } from '../components/restaurant/Alert';
import StorefrontIcon from '@mui/icons-material/Storefront';

export async function loaderById({ params }) {
  const { restaurantId } = params;

  return loadCompanyById(restaurantId);

}

export async function loaderByName({ params }) {
  const { restaurantName } = params;

  return loadCompanyByName(restaurantName);

}

export default function Restaurant() {
  const company = useLoaderData();
  const { seatId } = useParams();
  const { t, i18n } = useTranslation();
  const [value, setValue] = useState(0);
  const [order, setOrder] = useState([]);
  const [payment, setPayment] = useState(null);
  const orderButtonRef = useRef(null);
  const [showFab, setShowFab] = useState(false);
  const { openDialog, closeDialog, isOpen } = useDialog();

  const menu = useMemo(() => {
    if (!company.menus.length) return null;

    return (
      company.menus.find(menu => menu.language === i18n.language) ||
      company.menus[0]
    );
  }, [company.menus, i18n.language]);

  const seat = useMemo(() => {
    if (!seatId) {
      return null;
    }
    return company.seats.find(seat => seat._id === decodeBase64UUID(seatId))
  
  }, [company.seats, seatId]);
  
  const readOnly = useMemo(() => !seatId, [seatId]);
  const notAvailable = useMemo(() => menu.language !== i18n.language, [menu.language, i18n.language]);

  const handleChange = useCallback((e, newValue) => {
    e.preventDefault();
    setValue(newValue);
  }, []);

  useEffect(() => {
    const target = orderButtonRef.current;

    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFab(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
      }
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
    };
  }, []);

  return (
    <Box sx={{ mx: 'auto', width: '100%', maxWidth: {md: '1024px'}, p: 1 }}>
      {/*Header: Seat + Logo + Lang + Help*/}
      <Header
        company={company}
        seat={seat}
        readOnly={readOnly}/>
      {/* FAB when the main button is out of view */}
      {showFab && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
          }}>
          <Badge
            badgeContent={order.reduce((total, item) => total + item.quantity, 0)}
            color="error"
            overlap="circular"
            invisible={order.length === 0}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              zIndex: 1400, // ensure badge is above the Fab
              '& .MuiBadge-badge': {
                zIndex: 1401,
              },
            }}>
            <Fab
              color="primary"
              onClick={() => openDialog('orderConfirmation')}
              disabled={order.length === 0}>
              <StorefrontIcon />
            </Fab>
          </Badge>
        </Box>
      )}

      <Alerts noMenu={notAvailable} readOnly={readOnly}/>

      {/*Body: Tabs + Panels + Order button*/}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
      }}>
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{my: 1}}>
          {menu.categories.map((category, index) => (
            <Tab
              key={index} label={category.name}/>
          ))}
        </Tabs>
      </Box>
      {menu.categories[value] && (
        <TabPanel
          items={menu.categories[value].items}
          order={order}
          setOrder={setOrder}
          readOnly={readOnly}
          reducedTax={menu.categories[value].reducedTax}
          excludeTax={menu.categories[value].excludeTax}/>
      )}

      <Box sx={{ p: 1 }}>
        <Button
          fullWidth
          ref={orderButtonRef}
          variant="contained"
          color="primary"
          startIcon={<StorefrontIcon />}
          size="large"
          onClick={() => openDialog('orderConfirmation')}
          disabled={order.length === 0}>
          {t('orderButton')}
        </Button>
      </Box>

      {/*Footer: Disclaimers*/}
      <Footer/>

      {/*Dialogs: Order + Payment + Status*/}
      {isOpen('orderConfirmation') && order.length > 0 && (<OrderDialog
        company={company}
        seat={seat}
        order={order}
        setPayment={setPayment}
        isOpen={isOpen}
        openDialog={openDialog}
        closeDialog={closeDialog}/>
      )}

      {isOpen('tipSelection') && order.length > 0 && payment != null && (<TipDialog
        company={company}
        seat={seat}      
        order={order}
        payment={payment}
        setPayment={setPayment}
        isOpen={isOpen}
        openDialog={openDialog}
        closeDialog={closeDialog}/>
      )}

      {isOpen('paymentMethod') && order.length > 0 && payment != null && (<PaymentDialog
        company={company}
        seat={seat}      
        order={order}
        payment={payment}
        setPayment={setPayment}
        isOpen={isOpen}
        openDialog={openDialog}
        closeDialog={closeDialog}/>
      )}

      {isOpen('paymentStatus') && order.length > 0 && payment != null && (<StatusDialog
        order={order}
        setOrder={setOrder}
        payment={payment}
        setPayment={setPayment}
        isOpen={isOpen}
        openDialog={openDialog}
        closeDialog={closeDialog}/>
      )}
    </Box>
  );
}

const TabPanel = memo(({
  items,
  order,
  setOrder,
  readOnly,
  excludeTax,
  reducedTax
}) => {
  return (
      <Paper elevation={3}
          sx={{
            borderRadius: 2,
            width: '100%',
            p: 2
        }}>
        <Stack spacing={0} divider={<Divider sx={{ mx: 2 }} />}>
          {items.map((item) => (
            <MenuItem
              key={item._id}
              item={item}
              order={order}
              setOrder={setOrder}
              readOnly={readOnly}
              excludeTax={excludeTax}
              reducedTax={reducedTax}
            />
          ))}
        </Stack>
      </Paper>
  );
});
